import { TextFieldModule } from '@angular/cdk/text-field';
import { Component, computed, EventEmitter, input, Input, InputSignal, OnInit, Output, Signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';

import { UntilDestroy } from '@ngneat/until-destroy';
import { Subscription } from 'rxjs';
import { pairwise, tap } from 'rxjs/operators';

import { FormUtils } from 'tiime-expert-utils';
import {
  TiimeChipsModule,
  TiimeFormModule,
  TiimeInputContainerModule,
  TiimeRichTextEditorModule,
  TiimeSnackbarService
} from 'tiime-material';

import { GIFileType } from '@enums/gi-file-type.enum';
import { EmailAttachmentForm } from '@forms/email-attachment-form';
import { EmailForm } from '@forms/email-form';
import { ContractEmailAttachmentForm } from '@gi/forms/contract-email-attachment-form';
import { ArithmeticHelper } from '@helpers/arithmetic.helper';
import { FileHelper } from '@helpers/file.helper';
import { PhoneHelper } from '@helpers/phone.helper';
import { CodeAndLabel } from '@models/code-and-label';
import { EmailFrom } from '@models/email-from';
import { PieceFile } from '@models/piece-file';
import { UPLOAD_FILE_CONFIG } from '@modules/shared/overlays/upload-file-overlay/upload-file-config.constant';
import { AddFilesButtonComponent } from '@shared-components/add-files-button/add-files-button.component';
import { AttachmentItemComponent } from '@shared-components/attachment-item/attachment-item.component';
import { MultipleAttachmentsSelectorComponent } from '@shared-components/multiple-attachments-selector/multiple-attachments-selector.component';
import { EmailAddressFormComponent } from '@shared-forms/email-address-form/email-address-form.component';
import { GenericEmailSelectComponent } from '@shared-selectors/generic-email-select/generic-email-select.component';

@UntilDestroy({ checkProperties: true })
@Component({
  standalone: true,
  imports: [
    TiimeInputContainerModule,
    ReactiveFormsModule,
    TiimeFormModule,
    EmailAddressFormComponent,
    GenericEmailSelectComponent,
    TextFieldModule,
    AddFilesButtonComponent,
    MultipleAttachmentsSelectorComponent,
    AttachmentItemComponent,
    TiimeChipsModule,
    TiimeRichTextEditorModule
  ],
  selector: 'app-email-form',
  templateUrl: './email-form.component.html',
  styleUrls: ['./email-form.component.scss']
})
export class EmailFormComponent implements OnInit {
  readonly fromEmails: InputSignal<EmailFrom[]> = input<EmailFrom[]>([]);
  @Input() form: EmailForm;
  @Input() displayTo = true;
  @Input() displayCc = false;
  @Input() displayCci = false;
  @Input() messageMinRows = 7;
  @Input() autosize = true;
  @Input() maximumSize = 25;
  @Input() displayAddAttachment = true;
  @Input() canSelectAttachments = false;
  @Input() contractFileTypes: CodeAndLabel<GIFileType>[];
  readonly withRichTextEditor: InputSignal<boolean> = input(false);

  @Output() readonly loading: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  readonly froms: Signal<string[]> = computed(() => this.fromEmails().map((from: EmailFrom) => from.email));
  protected readonly acceptedTypes = UPLOAD_FILE_CONFIG.acceptedTypes;

  private readonly subscription: Subscription = new Subscription();

  constructor(private snackbarService: TiimeSnackbarService) {}

  ngOnInit(): void {
    this.observeFrom();
  }

  onRemoveAttachment(index: number): void {
    this.form.attachments.removeAt(index);
  }

  onFilesAdded(files: PieceFile[]): void {
    const currentFileSize = this.form.attachments.controls
      .filter((attachment: EmailAttachmentForm) => attachment.file.value?.size)
      .reduce(
        (acc: number, attachment: EmailAttachmentForm) =>
          ArithmeticHelper.plus(acc, attachment.file.value.size / 1048576),
        0
      );

    const totalFileSize = files.reduce(
      (acc: number, file: PieceFile) => ArithmeticHelper.plus(acc, file.file.size / 1048576),
      currentFileSize
    );

    if (totalFileSize > this.maximumSize) {
      this.snackbarService.openError(`La taille totale des fichiers ne doit pas dépasser ${this.maximumSize}Mo.`);
    } else {
      files.forEach(file =>
        this.getFileContent(file).then(emailAttachmentForm => this.form.attachments.push(emailAttachmentForm))
      );
    }
  }

  private async getFileContent(file: PieceFile): Promise<EmailAttachmentForm | ContractEmailAttachmentForm> {
    this.loading.emit(true);
    return FileHelper.fileToBase64(file.file).then(content => {
      const emailAttachmentForm: ContractEmailAttachmentForm | EmailAttachmentForm = !!this.contractFileTypes?.length
        ? new ContractEmailAttachmentForm()
        : new EmailAttachmentForm();

      emailAttachmentForm.patchValue({
        file: file.file,
        type: file.mimeType,
        name: file.name,
        content,
        contractFileType: GIFileType.other
      });
      this.loading.emit(false);
      return emailAttachmentForm;
    });
  }

  private observeFrom(): void {
    this.subscription.add(
      this.form.from.valueChanges
        .pipe(
          pairwise(),
          tap(([previousFrom, currentFrom]: [string, string]) => {
            const lastEmailFrom: EmailFrom = this.fromEmails().find((from: EmailFrom) => from.email === previousFrom);
            const currentEmailFrom: EmailFrom = this.fromEmails().find((from: EmailFrom) => from.email === currentFrom);

            if (!lastEmailFrom || !currentEmailFrom) {
              return;
            }

            const stringToReplace: string = lastEmailFrom?.phoneNumber
              ? `${lastEmailFrom?.name} - ${PhoneHelper.format(lastEmailFrom?.phoneNumber)} - ${lastEmailFrom?.email}`
              : `${lastEmailFrom?.name} - ${lastEmailFrom?.email}`;

            const newString: string = currentEmailFrom?.phoneNumber
              ? `${currentEmailFrom?.name} - ${PhoneHelper.format(currentEmailFrom?.phoneNumber)} - ${currentEmailFrom?.email}`
              : `${currentEmailFrom?.name} - ${currentEmailFrom?.email}`;

            this.form.body.patchValue(
              this.form.body.value.replace(stringToReplace, newString),
              FormUtils.shouldNotEmitEvent
            );
          })
        )
        .subscribe()
    );
  }
}
